import React, {
 useContext, useEffect, useMemo, useState,
} from 'react';
import ContentLoader from 'react-content-loader';
import { useTranslation } from 'react-i18next';
import { useLoaderData } from 'react-router-dom';
import { useImmer } from 'use-immer';

import Location from '@r6digital/rapidstor-types/react/entities/Location';
import { MarketingSurveyPayload } from '@r6digital/rapidstor-types/react/entities/MarketingSource';
import { MarketingSurveyQuestion } from '@r6digital/rapidstor-types/types/Site';

import {
    useGetMarketingSourcesQuery,
    usePostMarketingSurveyMutation,
} from '@/core/api/rapidstor';
import Logger from '@/core/utils/logging';

import Alert from '@/core/components/elements/alert';
import Button from '@/core/components/elements/button';
import { JourneyContext } from '@/features/rapidstor/providers/JourneyProvider';

import classes from './styles.module.scss';

interface Props {
    marketingSurvey: {
        use: boolean;
        questions: {
            [key: string]: MarketingSurveyQuestion;
        };
    };
    tenantId: number | null | undefined;
}

export default function MarketingSurvey({ marketingSurvey, tenantId }: Props) {
    const location = useLoaderData() as Location;
    const { t } = useTranslation();
    const { rapidstorMainColor } = useContext(JourneyContext);

    const [marketingErrors, setMarketingErrors] = useState<string[]>([]);
    const [marketingPosting, setMarketingPosting] = useState<boolean>(false);
    const [marketingPostError, setMarketingPostError] = useState<string | null>(null);
    const [marketingSurveyComplete, setMarketingSurveyComplete] = useState<boolean>(false);

    const [postMarketingSurvey] = usePostMarketingSurveyMutation();

    const { data: marketingSources, isLoading: marketingLoading } = useGetMarketingSourcesQuery({
        locationCode: location.locationCode,
        corpCode: location.corpCode,
    });

    const [form, updateForm] = useImmer<Record<string, number>>({
        whereHearAboutUs: 0,
        distanceFromFacility: 0,
        whyStoring: 0,
        customerType: 0,
        whatStoring: 0,
        whyStoringWithUs: 0,
    });

    const filteredSources = useMemo(() => {
        if (!marketingSources) {
            return [];
        }
        return marketingSources[location.locationCode];
    }, [marketingSources, location.locationCode]);

    const submitMarketingSurvey = async () => {
        setMarketingErrors([]);
        const errors: string[] = [];

        if (
            marketingSurvey.questions.whereHearAboutUs.required
            && form.whereHearAboutUs === 0
        ) {
            errors.push('whereHearAboutUs');
        }

        if (
            marketingSurvey.questions.distanceFromFacility.required
            && form.distanceFromFacility === 0
        ) {
            errors.push('distanceFromFacility');
        }

        if (marketingSurvey.questions.whyStoring.required && form.whyStoring === 0) {
            errors.push('whyStoring');
        }

        if (marketingSurvey.questions.customerType.required && form.customerType === 0) {
            errors.push('customerType');
        }

        if (marketingSurvey.questions.whatStoring.required && form.whatStoring === 0) {
            errors.push('whatStoring');
        }

        if (
            marketingSurvey.questions.whyStoringWithUs.required
            && form.whyStoringWithUs === 0
        ) {
            errors.push('whyStoringWithUs');
        }

        if (errors.length) {
            setMarketingErrors(errors);
            return;
        }

        if (!tenantId) {
            Logger.warn('[submitMarketingSurvey] Tenant ID not found', location);
            return;
        }

        const payload: MarketingSurveyPayload = {
            tenantId,
            survey: {
                whereHearAboutUs: form.whereHearAboutUs,
                distanceFromFacility: form.distanceFromFacility,
                whyStoring: form.whyStoring,
                customerType: form.customerType,
                whatStoring: form.whatStoring,
                whyStoringWithUs: form.whyStoringWithUs,
            },
        };

        try {
            setMarketingPosting(true);
            await postMarketingSurvey({
                locationCode: location.locationCode,
                corpCode: location.corpCode,
                tenantId,
                payload,
            }).unwrap();
            setMarketingErrors([]);
            setMarketingSurveyComplete(true);
        } catch (error) {
            setMarketingPostError(JSON.stringify(error));
            setMarketingPosting(false);
            Logger.error(
                `[submitMarketingSurvey] Error submitting marketing survey: ${JSON.stringify(
                    error,
                )}`,
                location,
                [
                    {
                        name: 'tenantId',
                        value: tenantId,
                    },
                ],
            );
        }
    };

    useEffect(() => {
        if (marketingSurveyComplete && location.rapidstor.moveinRedirectUrl) {
            window.location.href = location.rapidstor.moveinRedirectUrl;
        }
    }, [marketingSurveyComplete, location.rapidstor.moveinRedirectUrl]);

    return (
        <div className={`${classes.form}`}>
            <span>{t('wizard:marketingSurvey:title')}</span>
            <p>{t('wizard:marketingSurvey:subtitle')}</p>
            {marketingLoading ? (
                <ContentLoader
                    foregroundColor={rapidstorMainColor ?? location.skin.mainColour}
                    backgroundColor="#fff"
                    width="100%"
                    height="500px"
                >
                    <rect x="0" y="0" rx="3" ry="3" width="25%" height="28" />
                    <rect x="0" y="34" rx="3" ry="3" width="100%" height="50" />

                    <rect x="0" y="94" rx="3" ry="3" width="25%" height="28" />
                    <rect x="0" y="128" rx="3" ry="3" width="100%" height="50" />

                    <rect x="0" y="188" rx="3" ry="3" width="25%" height="28" />
                    <rect x="0" y="222" rx="3" ry="3" width="100%" height="50" />

                    <rect x="0" y="282" rx="3" ry="3" width="25%" height="28" />
                    <rect x="0" y="316" rx="3" ry="3" width="100%" height="50" />

                    <rect x="0" y="380" rx="3" ry="3" width="25%" height="28" />
                    <rect x="0" y="414" rx="3" ry="3" width="100%" height="50" />
                </ContentLoader>
            ) : null}
            {!marketingLoading && marketingSurveyComplete && !location.rapidstor.moveinRedirectUrl ? (
                <Alert
                    type="success"
                    text="Thank you for completing our customer survey"
                />
            ) : null}
            {!marketingLoading && !marketingSurveyComplete ? (
                <div className={classes.questions}>
                    <div className={`${classes.question}`}>
                        <label>{t('wizard:marketingSurvey:Marketing')}</label>
                        <select
                            onChange={(e) => {
                                updateForm((draft) => {
                                    draft.whereHearAboutUs = parseInt(
                                        e.target.value,
                                    );
                                });
                            }}
                            value={form.whereHearAboutUs}
                            className={`${
                                marketingErrors.includes('whereHearAboutUs')
                            && classes.invalid
                            }`}
                            disabled={marketingPosting}
                        >
                            <option value={0}>{t('wizard:marketingSurvey:defaultOption')}</option>
                            {filteredSources
                                .filter((source) => !source.deleted)
                                .filter((source) => source.prefix === 'Marketing')
                                .map((source) => (
                                    <option
                                        key={source.MarketingID}
                                        value={source.MarketingID}
                                    >
                                        {source.sMarketingDesc}
                                    </option>
                                ))}
                        </select>
                    </div>

                    <div className={`${classes.question}`}>
                        <label>{t('wizard:marketingSurvey:MktgDistance')}</label>
                        <select
                            onChange={(e) => {
                                updateForm((draft) => {
                                    draft.distanceFromFacility = parseInt(
                                        e.target.value,
                                    );
                                });
                            }}
                            value={form.distanceFromFacility}
                            className={`${
                                marketingErrors.includes(
                                    'distanceFromFacility',
                                ) && classes.invalid
                            }`}
                            disabled={marketingPosting}
                        >
                            <option value={0}>{t('wizard:marketingSurvey:defaultOption')}</option>
                            {filteredSources
                                .filter((source) => !source.deleted)
                                .filter(
                                    (source) => source.prefix === 'MktgDistance',
                                )
                                .map((source) => (
                                    <option
                                        key={source.MarketingID}
                                        value={source.MarketingID}
                                    >
                                        {source.sMktgDistanceDesc}
                                    </option>
                                ))}
                        </select>
                    </div>

                    <div className={`${classes.question}`}>
                        <label>{t('wizard:marketingSurvey:MktgWhy')}</label>
                        <select
                            onChange={(e) => {
                                updateForm((draft) => {
                                    draft.whyStoring = parseInt(e.target.value);
                                });
                            }}
                            value={form.whyStoring}
                            className={`${
                                marketingErrors.includes('whyStoring')
                            && classes.invalid
                            }`}
                            disabled={marketingPosting}
                        >
                            <option value={0}>{t('wizard:marketingSurvey:defaultOption')}</option>
                            {filteredSources
                                .filter((source) => !source.deleted)
                                .filter((source) => source.prefix === 'MktgWhy')
                                .map((source) => (
                                    <option
                                        key={source.MarketingID}
                                        value={source.MarketingID}
                                    >
                                        {source.sMktgWhyDesc}
                                    </option>
                                ))}
                        </select>
                    </div>

                    <div className={`${classes.question}`}>
                        <label>{t('wizard:marketingSurvey:MktgType')}</label>
                        <select
                            onChange={(e) => {
                                updateForm((draft) => {
                                    draft.customerType = parseInt(e.target.value);
                                });
                            }}
                            value={form.customerType}
                            className={`${
                                marketingErrors.includes('customerType')
                            && classes.invalid
                            }`}
                            disabled={marketingPosting}
                        >
                            <option value={0}>{t('wizard:marketingSurvey:defaultOption')}</option>
                            {filteredSources
                                .filter((source) => !source.deleted)
                                .filter((source) => source.prefix === 'MktgType')
                                .map((source) => (
                                    <option
                                        key={source.MarketingID}
                                        value={source.MarketingID}
                                    >
                                        {source.sMktgTypeDesc}
                                    </option>
                                ))}
                        </select>
                    </div>

                    <div className={`${classes.question}`}>
                        <label>{t('wizard:marketingSurvey:MktgWhat')}</label>
                        <select
                            onChange={(e) => {
                                updateForm((draft) => {
                                    draft.whatStoring = parseInt(e.target.value);
                                });
                            }}
                            value={form.whatStoring}
                            className={`${
                                marketingErrors.includes('whatStoring')
                            && classes.invalid
                            }`}
                            disabled={marketingPosting}
                        >
                            <option value={0}>{t('wizard:marketingSurvey:defaultOption')}</option>
                            {filteredSources
                                .filter((source) => !source.deleted)
                                .filter((source) => source.prefix === 'MktgWhat')
                                .map((source) => (
                                    <option
                                        key={source.MarketingID}
                                        value={source.MarketingID}
                                    >
                                        {source.sMktgWhatDesc}
                                    </option>
                                ))}
                        </select>
                    </div>

                    <div className={`${classes.question}`}>
                        <label>{t('wizard:marketingSurvey:MktgReason')}</label>
                        <select
                            onChange={(e) => {
                                updateForm((draft) => {
                                    draft.whyStoringWithUs = parseInt(
                                        e.target.value,
                                    );
                                });
                            }}
                            value={form.whyStoringWithUs}
                            className={`${
                                marketingErrors.includes('whyStoringWithUs')
                            && classes.invalid
                            }`}
                            disabled={marketingPosting}
                        >
                            <option value={0}>{t('wizard:marketingSurvey:defaultOption')}</option>
                            {filteredSources
                                .filter((source) => !source.deleted)
                                .filter(
                                    (source) => source.prefix === 'MktgReason',
                                )
                                .map((source) => (
                                    <option
                                        key={source.MarketingID}
                                        value={source.MarketingID}
                                    >
                                        {source.sMktgReasonDesc}
                                    </option>
                                ))}
                        </select>
                    </div>

                    {marketingPostError && (
                        <Alert type="error" text={marketingPostError} />
                    )}

                    <Button
                        style={{ marginTop: '12px' }}
                        type="primary"
                        size="small"
                        onclick={submitMarketingSurvey}
                        disableScroll
                        disabled={marketingPosting}
                    >
                        {t('wizard:marketingSurvey:submit')}
                    </Button>
                </div>
            ) : null}
        </div>
    );
}
